// src/auth/routeAccess.ts
// Single source of truth for "which view needs which permission".
import { matchPath } from 'react-router-dom';
import type { KnownFeature, UserRole } from './roles';

export type Requirement =
    | { kind: 'public' }
    | { kind: 'signedIn' }
    | { kind: 'admin' }
    | { kind: 'feature'; anyOf: KnownFeature[] };

const feature = (...anyOf: KnownFeature[]): Requirement => ({
    kind: 'feature',
    anyOf,
});
const ADMIN: Requirement = { kind: 'admin' };
const SIGNED_IN: Requirement = { kind: 'signedIn' };
const PUBLIC: Requirement = { kind: 'public' };

export const ROUTE_ACCESS: ReadonlyArray<{
    path: string;
    require: Requirement;
}> = [
    { path: '/sign-up', require: PUBLIC },
    { path: '/set-password', require: PUBLIC },
    { path: '/forgot-password', require: PUBLIC },
    { path: '/change-temp-password', require: PUBLIC },
    { path: '/no-access', require: SIGNED_IN },

    { path: '/markets/asset/:assetType/:ticker', require: feature('assets') },
    { path: '/markets/asset/:ticker', require: feature('assets') },
    { path: '/markets/search', require: feature('events') },
    { path: '/markets/event/:eventId', require: feature('events') },
    { path: '/markets/market/:id', require: feature('events') },
    { path: '/markets', require: feature('assets', 'events') },
    { path: '/', require: feature('assets', 'events') },

    { path: '/markets/trader/:address', require: feature('events') },
    { path: '/screener', require: feature('assets', 'events') },
    { path: '/screener_simulation', require: feature('simulation') },
    { path: '/portfolio', require: feature('portfolio') },
    { path: '/monitor', require: feature('monitor') },
    { path: '/monitor/pipr', require: feature('monitor') },
    { path: '/monitor/pipr/:bank', require: feature('monitor') },

    { path: '/watchlists', require: ADMIN },
    { path: '/chats', require: ADMIN },
    { path: '/chats/new', require: ADMIN },
    { path: '/chat/:conversationId', require: ADMIN },

    { path: '/gates', require: SIGNED_IN },
    { path: '/settings', require: SIGNED_IN },
    { path: '/settings/notifications', require: SIGNED_IN },

    { path: '/admin/users', require: ADMIN },
    { path: '/admin/users/:id', require: ADMIN },
    { path: '/admin/subscribers', require: ADMIN },
    { path: '/admin/audit', require: ADMIN },
    { path: '/admin/gates', require: ADMIN },
];

// Static segments beat params, longer patterns beat shorter ones.
function specificity(pattern: string): number {
    const segs = pattern.split('/').filter(Boolean);
    return segs.length * 10 + segs.filter(s => !s.startsWith(':')).length;
}

const ORDERED = [...ROUTE_ACCESS].sort(
    (a, b) => specificity(b.path) - specificity(a.path)
);

// Unmatched paths resolve to admin-only: a new route is denied until listed.
export function requirementForPath(pathname: string): Requirement {
    for (const entry of ORDERED) {
        if (matchPath({ path: entry.path, end: true }, pathname)) {
            return entry.require;
        }
    }
    return ADMIN;
}

export function isAllowed(
    req: Requirement,
    role: UserRole | undefined,
    features: readonly string[]
): boolean {
    if (req.kind === 'public') return true;
    if (!role) return false;
    if (role === 'admin') return true;
    if (req.kind === 'admin') return false;
    if (req.kind === 'signedIn') return true;
    return req.anyOf.some(f => features.includes(f));
}
